import type { Asset, Slide, StencilMask, TextAsset, TimeAwareMediaAsset } from '@lumen5/luminary';
import type Luminary from '@lumen5/luminary';
import { getGlobalFontManager, VideoData } from '@lumen5/luminary';

import { MediaRegistry } from './registries/MediaRegistry';

import { MusicRegistry } from 'js/src/agent/registries/MusicRegistry';
import { getColorName } from 'js/src/agent/utils';

/**
 * Parse text with **highlight** markdown format and return clean text and highlight positions
 */
export function getTextAndHighlightsFromMarkdown(markdown: string): {
    text: string;
    highlights: [number, number][];
} {
    const highlights: [number, number][] = [];
    let text = '';
    let currentPosition = 0;

    // Find all **text** patterns
    const highlightRegex = /\*\*([^*]+)\*\*/g;
    let match;
    let lastIndex = 0;

    while ((match = highlightRegex.exec(markdown)) !== null) {
        const beforeHighlight = markdown.slice(lastIndex, match.index);
        const highlightedText = match[1];

        // Add text before highlight to clean text
        text += beforeHighlight;
        currentPosition += beforeHighlight.length;

        // Record highlight start and end positions
        const start = currentPosition;
        const end = currentPosition + highlightedText.length;
        highlights.push([start, end]);

        // Add highlighted text to clean text
        text += highlightedText;
        currentPosition += highlightedText.length;

        lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last highlight
    text += markdown.slice(lastIndex);

    // Ensure highlights are sorted by start position
    highlights.sort((a, b) => a[0] - b[0]);

    return { text, highlights };
}

/**
 * Get the text content and highlights from a TextAsset and convert it to markdown format
 */
export function getTextAndHighlightsAsMarkdown(asset: TextAsset): string {
    let markdown = asset.text;

    // Apply highlights from the end to keep the indices valid
    asset.textHighlights
        .sort((a, b) => b[1] - a[1])
        .forEach(([start, end]) => {
            // Slice the text into three parts: before, highlight, after
            const before = markdown.slice(0, start);
            const highlight = markdown.slice(start, end);
            const after = markdown.slice(end);

            // Apply markdown bold syntax to the highlight and reconstruct the string
            // we replace all tabs (\t), newlines (\n), and carriage returns (\r) from the start
            // and end of the highlight. We leave spaces to easily support subword highlights.
            markdown =
                before + '**' + highlight.replace(/^[\t\n\r]+|[\t\n\r]+$/g, '') + '**' + after;
        });

    return markdown;
}

/** Position an asset at a specific position, taking into account its anchor point. */
export function positionAsset(asset: Asset, position: { x: number; y: number }): void {
    const { width, height } = asset.getBounds();
    const anchorXOffset = asset.transformation.anchor.x * width;
    const anchorYOffset = asset.transformation.anchor.y * height;
    asset.transformation.position.x = position.x + anchorXOffset;
    asset.transformation.position.y = position.y + anchorYOffset;
}

export function getTemplateFontFamilies(luminary: Luminary): string[] {
    // Get the global font manager to access all available fonts
    const fontManager = getGlobalFontManager();
    const templateFonts = luminary.template?.fonts || [];

    // Filter and return only the fonts that are actually used in the template
    const fontFamilies = templateFonts
        .map((url) => {
            let font = fontManager.getFontBySource(url);
            if (!font) {
                // Luminary only loads fonts when building the asset
                font = fontManager.addFont(url);
            }
            return font ? font.familyName : null;
        })
        .filter((font) => font !== null);

    return fontFamilies;
}

export function getTemplateFontFamily(luminary: Luminary, fontURL: string): string | null {
    // Get the global font manager to access all available fonts
    const fontManager = getGlobalFontManager();
    const templateFonts = luminary.template?.fonts || [];

    // Find the first font that matches the requested URL
    for (const url of templateFonts) {
        const font = fontManager.getFontBySource(url);
        if (font && url === fontURL) {
            return font.familyName;
        }
    }

    return null; // No matching font found
}

export function getTemplateFontURL(luminary: Luminary, fontFamily: string): string | null {
    // Get the global font manager to access all available fonts
    const fontManager = getGlobalFontManager();
    const templateFonts = luminary.template?.fonts || [];

    // Find the first font that matches the requested family
    for (const url of templateFonts) {
        const font = fontManager.getFontBySource(url);
        if (font && font.familyName === fontFamily) {
            return url;
        }
    }

    return null; // No matching font found
}

const renderColorAsString = (color: number): string => {
    return `${color}(${getColorName(color)})`;
};

const simplifyAssetType = (asset: Asset): string => {
    // Handle stickers first - they are a special case of media assets
    if (VideoData.isMediaAsset(asset) && VideoData.isStickerAsset(asset)) {
        return 'STICKER';
    }

    // Then check classType for regular asset types
    switch (asset.classType) {
        case VideoData.TEXT_ASSET_TYPE:
        case 'TextAsset':
            if (!asset.isEditable) {
                return 'READONLY_TEXT';
            }
            return 'TEXT';
        case VideoData.FANCY_CAPTION_ASSET_TYPE:
        case 'FancyCaptionAsset':
        case VideoData.CAPTION_ASSET_TYPE:
            return 'CAPTION';
        case VideoData.MEDIA_ASSET_TYPE:
            if (!asset.isEditable) {
                return 'READONLY_MEDIA';
            }
            return 'MEDIA';
        case VideoData.ICON_ASSET_TYPE:
            if (!asset.isEditable) {
                return 'READONLY_ICON';
            }
            return 'ICON';
        case VideoData.COLOR_ASSET_TYPE:
            return 'BACKGROUND';
        case VideoData.GROUP_ASSET_TYPE:
            return 'GROUP';
        case 'WatermarkAsset':
            return 'WATERMARK';
        default:
            return asset.classType;
    }
};

export const getVideoAnnotationGuide = () => {
    return `## Video Context:
Before each user message, you will receive an assistant message containing the current video structure with scene screenshots. This provides you with the complete video context.

The video annotation format describes the video structure:
${getVideoAnnotationFormat()}

Each asset follows specific annotation syntax:
${getAssetAnnotationFormat()}

Scene indexes are 0-based but users are shown 1-based indexes in the UI. When referring to scenes, always use 0-based indexing internally and 1-based indexing when communicating with users.`;
};

export const getVideoAnnotationFormat = () => {
    return `
- SCENE <index> contains assets that define the layout and content
- Asset types include:
  - TEXT: text rendered on the scene with size, max_chars, and editability
  - FANCY_CAPTION: text rendered on the scene with size driven by transcript
  - MEDIA: image or video displayed on the scene with dimensions and editability
  - ICON: icon displayed on the scene with dimensions and editability
  - STICKER: sticker displayed on the scene with dimensions and editability
  - GROUP: alignment container for child assets (not visible)
  - WATERMARK: watermark image displayed on the scene
  - BACKGROUND: solid background color with children`;
};

export const getVideoDescription = (luminary: Luminary) => {
    return {
        resolution: `${luminary.referenceResolution.width}x${luminary.referenceResolution.height}`,
        aspectRatio: luminary.aspectRatio,
        sceneCount: luminary.scenes.length,
        duration: `${luminary.duration}ms`,
    };
};

/**
 * Get template metadata including ID, fonts, and color palette.
 * @param luminary The Luminary instance containing template data
 * @returns Object with template ID, font families, and color descriptions
 */
export const getTemplateDescription = (luminary: Luminary) => {
    return {
        template: luminary.template.id,
        templateFonts: getTemplateFontFamilies(luminary).join(', '),
        templateColors: luminary.template.colors
            .map((item) => renderColorAsString(item.color))
            .join(', '),
    };
};

export const getVideoMusicDescription = (luminary: Luminary) => {
    if (luminary.music && luminary.music.uuid) {
        const musicRegistry = MusicRegistry.getInstance();
        const musicId = musicRegistry.getItemIdByUuid(luminary.music.uuid);
        if (musicId) {
            const musicRepresentation = musicRegistry.getRepresentation(musicId);
            if (musicRepresentation) {
                return {
                    ...musicRepresentation,
                    volume: luminary.musicAsset.volume,
                };
            }
        }
    }
    return 'No music set';
};

type BaseAssetDescription = {
    name: string;
    type: string;
    bounds: string;
};

type AssetDescription =
    | BaseAssetDescription
    | ColorAssetDescription
    | GroupAssetDescription
    | IconAssetDescription
    | TextAssetDescription
    | MediaAssetDescription
    | StickerAssetDescription;

const colorAssetFormat = () => {
    return `
- type: BACKGROUND
  name: <asset_name>
  bounds: <x, y, width, height>
  color: <color as 24-bit integer>`;
};

type ColorAssetDescription = BaseAssetDescription & {
    color: string;
};

const CaptionFormat = () => {
    return `
- type: CAPTION
  name: <asset_name>
  bounds: <x, y, max_width, max_height>`;
};

export type CaptionDescription = BaseAssetDescription;

const textAssetFormat = () => {
    return `
- type: TEXT
  name: <asset_name>
  text: <text content>
  bounds: <x, y, max_width, max_height>
  textAlignment: <text alignment>
  textColors: <normal and highlight text color as 24-bit integer>
  fontFamily: <font family>
  fontSize: <font size>
  textLines: <number of text lines>`;
};

export type TextAssetDescription = BaseAssetDescription & {
    text: string;
    textAlignment: string;
    textColors: string;
    textLines: number;
    fontFamily: string;
    fontSize: number;
};

const iconAssetFormat = () => {
    return `
- type: ICON
  name: <asset_name>
  bounds: <x, y, width, height>
  iconColors: <comma-separated list of colors as 24-bit integers>`;
};

type IconAssetDescription = BaseAssetDescription & {
    iconColors: string;
};

const groupAssetFormat = () => {
    return `
- type: GROUP
  name: <asset_name>
  bounds: <x, y, width, height>
  children: <comma-separated list of child asset names>
  childrenAlignment: <child alignment>`;
};

const mediaAssetFormat = () => {
    return `
- type: MEDIA
  name: <asset_name>
  bounds: <x, y, width, height>
  mediaScale: <scale%>
  mediaResolution: <width, height>
  mediaTranslation: <x, y>
  mediaXTranslationRange: <min, max>
  mediaYTranslationRange: <min, max>
  mediaId: <media ID>
  mediaDescription: <description>
  mediaType: <image/video>`;
};

const stickerAssetFormat = () => {
    return `
- type: STICKER
  name: <asset_name>
  bounds: <x, y, width, height>`;
};

type GroupAssetDescription = BaseAssetDescription & {
    children: string;
    childrenAlignment: string;
};

export type MediaAssetDescription = BaseAssetDescription & {
    mediaId: string;
    mediaDescription: string;
    mediaType: string;
    // media size
    mediaScale: string;
    mediaResolution: string;
    // media translation
    mediaTranslation: string;
    mediaXTranslationRange: string;
    mediaYTranslationRange: string;
};

export type StickerAssetDescription = BaseAssetDescription & {
    mediaId: string;
    stickerDescription: string;
    stickerType: string;
    mediaScale: string;
    mediaResolution: string;
    mediaTranslation: string;
    mediaXTranslationRange: string;
    mediaYTranslationRange: string;
};

export const getAssetAnnotationFormat = () => {
    return (
        groupAssetFormat() +
        '\n' +
        textAssetFormat() +
        '\n' +
        iconAssetFormat() +
        '\n' +
        mediaAssetFormat() +
        '\n' +
        stickerAssetFormat() +
        '\n' +
        colorAssetFormat() +
        '\n' +
        CaptionFormat()
    );
};

/* Get the visible area of the given media asset */
const getMediaViewport = (
    asset: TimeAwareMediaAsset,
): { x: number; y: number; width: number; height: number } => {
    if (asset.mask) {
        return (asset.mask as StencilMask).getMaxBounds();
    }
    const { width, height } = asset.root.referenceResolution;
    return { x: 0, y: 0, width, height };
};

/* Get the visible bounds of the given media asset.  */
const getVisibleMediaBounds = (asset: TimeAwareMediaAsset) => {
    const { x, y, width, height } = getMediaViewport(asset);
    return `${x} ${y} ${width} ${height}`;
};

/**
 * Get the full media resolution of the given media asset. This can be different from the visible area which
 * is determined by the scene or mask.
 */
const getFullMediaResolution = (asset: TimeAwareMediaAsset): string => {
    return `${Math.round(asset.width)} ${Math.round(asset.height)}`;
};

/* Get the media translation in pixels. Since the media is always centered, these values are relative to the center of the visible area. */
export const getFullMediaTranslation = (asset: TimeAwareMediaAsset) => {
    const { width, height } = getMediaViewport(asset);
    // get translation in pixels and remove sign
    let xTranslation = Math.abs(asset.xTranslation * width);
    let yTranslation = Math.abs(asset.yTranslation * height);

    // due to rounding, it's possible for our x or y translation to be out of range.
    // even though this will be absorbed by Luminary, it's confusing to the user so we want to correct it here.
    if (xTranslation > (asset.width - width) / 2.0) {
        xTranslation = (asset.width - width) / 2.0;
    }
    if (yTranslation > (asset.height - height) / 2.0) {
        yTranslation = (asset.height - height) / 2.0;
    }
    // add sign correction
    if (asset.xTranslation < 0) {
        xTranslation *= -1;
    }
    if (asset.yTranslation < 0) {
        yTranslation *= -1;
    }

    // the model always seems to be confused between left/right and up/down even though we provide it as part of the setMedia tool description
    // we're going to re-iterate it here for clarity
    const xDirection =
        asset.xTranslation === 0 ? 'center' : asset.xTranslation < 0 ? 'right' : 'left';
    const yDirection = asset.yTranslation === 0 ? 'center' : asset.yTranslation < 0 ? 'down' : 'up';

    return `${Math.round(xTranslation)}(${xDirection}) ${Math.round(yTranslation)}(${yDirection})`;
};

export const setFullMediaTranslation = (
    asset: TimeAwareMediaAsset,
    pixelsInX: number,
    pixelsInY: number,
) => {
    // max translation limits
    const { width, height } = getMediaViewport(asset);
    const xLimit = (asset.width - width) / 2.0;
    const yLimit = (asset.height - height) / 2.0;
    if (Math.abs(pixelsInX) > xLimit) {
        throw new Error(
            `Requested x translation ${pixelsInX} pixels exceeds limit of ${xLimit} pixels`,
        );
    }
    if (Math.abs(pixelsInY) > yLimit) {
        throw new Error(
            `Requested y translation ${pixelsInY} pixels exceeds limit of ${yLimit} pixels`,
        );
    }
    asset.xTranslation = pixelsInX / asset.width;
    asset.yTranslation = pixelsInY / asset.height;
};

export const getFullMediaScale = (asset: TimeAwareMediaAsset) => {
    const scale = Math.round(asset.scaleX * 100); // scaleX and scaleY should always be the same
    return `${scale}%`;
};

/* Get the full media translation range in pixels. This corresponds to how much the media can move within the visible area with the current scale value. We can increase the range by increasing the scale. */
const getFullMediaTranslationRange = (asset: TimeAwareMediaAsset) => {
    const { width, height } = getMediaViewport(asset);
    const xRange = Math.round((asset.width - width) / 2.0);
    const yRange = Math.round((asset.height - height) / 2.0);

    // WARNING: since we're rounding our translation range values in Luminary, our media translation values can be larger then expected
    // const tx = (xRange / width).toFixed(2); <-
    // const ty = (yRange / height).toFixed(2); <-
    // console.log(`translation range: [${tx}, ${ty}]`);

    const mediaXTranslationRange = `-${xRange} ${xRange}`;
    const mediaYTranslationRange = `-${yRange} ${yRange}`;

    return { mediaXTranslationRange, mediaYTranslationRange };
};

export const getSceneDescriptions = (scene: Slide) => {
    const hasSubscenes = scene.subscenes.length > 1;
    const textPositionPresets = scene.availableTextPositions;

    let captionAsset;
    if (scene.sceneGroup.captions && scene.sceneGroup.captions.asset) {
        const asset = scene.sceneGroup.captions.asset;
        captionAsset = {
            name: asset.name,
            type: simplifyAssetType(asset),
            bounds: `${Math.round(asset.getBounds().x)} ${Math.round(asset.getBounds().y)} ${Math.round(asset.getBounds().width)} ${Math.round(asset.getBounds().height)}`,
        };
    }

    const assets = [];
    if (hasSubscenes) {
        // For scenes with subscenes, collect assets from each subscene
        const subsceneAssets: (AssetDescription & { subscene: number })[] = [];

        for (let subsceneIndex = 0; subsceneIndex < scene.subscenes.length; subsceneIndex++) {
            const subscene = scene.subscenes[subsceneIndex];
            const subsceneAssetsData = subscene
                .getAssets()
                .map((asset) => {
                    if (VideoData.isLottieAsset(asset)) {
                        return;
                    }
                    const assetInfo: BaseAssetDescription & { subscene: number } = {
                        name: asset.name,
                        type: simplifyAssetType(asset),
                        bounds: `${Math.round(asset.getBounds().x)} ${Math.round(asset.getBounds().y)} ${Math.round(asset.getBounds().width)} ${Math.round(asset.getBounds().height)}`,
                        subscene: subsceneIndex,
                    };

                    // Apply the same asset-specific logic as the non-subscene case
                    const updatedAssetInfo = applyAssetSpecificInfo(asset, assetInfo);
                    return { ...updatedAssetInfo, subscene: subsceneIndex };
                })
                .filter(Boolean) as (AssetDescription & { subscene: number })[];
            subsceneAssets.push(...subsceneAssetsData);
        }

        if (captionAsset) {
            assets.push(captionAsset);
        }

        return {
            design: scene.sceneDesign,
            duration: `${scene.duration}ms`,
            subscenes: scene.subscenes.length,
            assets: subsceneAssets,
            textPositionPresets,
        };
    } else {
        // For scenes without subscenes, use the original logic
        const assets = scene
            .getAssets()
            .map((asset) => {
                if (VideoData.isLottieAsset(asset)) {
                    return;
                }
                let assetInfo: BaseAssetDescription = {
                    name: asset.name,
                    type: simplifyAssetType(asset),
                    bounds: `${Math.round(asset.getBounds().x)} ${Math.round(asset.getBounds().y)} ${Math.round(asset.getBounds().width)} ${Math.round(asset.getBounds().height)}`,
                };

                assetInfo = applyAssetSpecificInfo(asset, assetInfo);
                return assetInfo;
            })
            .filter(Boolean) as AssetDescription[];

        if (captionAsset) {
            assets.push(captionAsset);
        }

        return {
            design: scene.sceneDesign,
            duration: `${scene.duration}ms`,
            assets,
            textPositionPresets,
        };
    }
};

// Helper function to apply asset-specific information
function applyAssetSpecificInfo(asset: Asset, assetInfo: BaseAssetDescription): AssetDescription {
    // watermark assets are special media assets and should be handled separately since they are managed on the scene level through SceneWatermarkManager
    if (VideoData.isMediaAsset(asset) && asset.name === 'watermark_1') {
        assetInfo.type = 'WatermarkAsset';
    }

    if (VideoData.isColorAsset(asset)) {
        assetInfo = {
            ...assetInfo,
            color: renderColorAsString(asset.sourceColor),
        } as ColorAssetDescription;
    }

    if (VideoData.isAssetGroup(asset)) {
        const children = asset
            .getAssets()
            .map((child) => child.name)
            .join(', ');
        assetInfo = {
            ...assetInfo,
            children: children,
            childrenAlignment: asset.alignChildren,
        } as GroupAssetDescription;
    }

    if (VideoData.isIconAsset(asset)) {
        let colors = '';
        asset.colorMaps.forEach((colorMap, index, array) => {
            if (colors) {
                if (index === array.length - 1) {
                    colors += ' and ';
                } else {
                    colors += ', ';
                }
            }
            colors += renderColorAsString(colorMap.targetColor || 0);
        });
        assetInfo = {
            ...assetInfo,
            iconColors: colors,
        } as IconAssetDescription;
    }

    if (VideoData.isFancyCaptionAsset(asset)) {
        const textColors = asset.style.fill;
        const fontSize = Math.round(asset.textSize);
        const fontFamily = asset.style
            .getCanvasRendererSettings({
                leading: asset.style.leading,
                fontSize: asset.style.fontSize,
            })
            .fontFamily.split('-')[0];
        const textAlignment = asset.textAlign;
        const textLines = asset?.renderer?.activeText?.lines?.length ?? 0;
        const text = asset?.renderer?.activeText?.text ?? '';

        assetInfo = {
            ...assetInfo,
            text: text,
            textAlignment: textAlignment,
            textColors: renderColorAsString(textColors),
            textLines: textLines,
            fontSize: fontSize,
            fontFamily: fontFamily,
        } as TextAssetDescription;
    }

    if (VideoData.isTextAsset(asset)) {
        const baseColor = asset.normalStyle.fill;
        const highlightColor = asset.highlightStyle.fill;
        const textColors =
            highlightColor !== baseColor && asset.textHighlights.length > 0
                ? `${renderColorAsString(baseColor)} and ${renderColorAsString(highlightColor)}`
                : renderColorAsString(baseColor);
        const textLines = asset?.renderer?.metrics?.lines?.length ?? 0;

        assetInfo = {
            ...assetInfo,
            text: getTextAndHighlightsAsMarkdown(asset),
            textAlignment: asset.textAlign,
            textColors: textColors,
            textLines: textLines,
            fontSize: Math.round(asset.textSize),
            fontFamily: asset.normalStyle.getCanvasRendererSettings({
                leading: asset.normalStyle.leading,
                fontSize: asset.normalStyle.fontSize,
            }).fontFamily,
        } as TextAssetDescription;
    }

    if (VideoData.isMediaAsset(asset) && !VideoData.isStickerAsset(asset) && asset.sourceData) {
        const mediaRegistry = MediaRegistry.getInstance();
        const uuid = asset.sourceData.uuid;
        let mediaId = mediaRegistry.getItemIdByUuid(uuid);
        let mediaDescription = 'No description available';
        let mediaType = 'image';
        const bounds = getVisibleMediaBounds(asset);
        const mediaResolution = getFullMediaResolution(asset);
        const mediaScale = getFullMediaScale(asset);
        const mediaTranslation = getFullMediaTranslation(asset);
        const { mediaXTranslationRange, mediaYTranslationRange } =
            getFullMediaTranslationRange(asset);

        if (!mediaId) {
            mediaId = '<unknown>';
        } else {
            const representation = mediaRegistry.getRepresentation(mediaId);
            if (representation) {
                mediaDescription =
                    representation.title ||
                    representation.description ||
                    'No description available';
                mediaType = representation.type;
            }
        }

        assetInfo = {
            ...assetInfo,
            mediaId,
            mediaDescription,
            mediaType,
            bounds,
            mediaResolution,
            mediaScale,
            mediaTranslation,
            mediaXTranslationRange,
            mediaYTranslationRange,
        } as MediaAssetDescription;
    }

    return assetInfo;
}
